<script lang="ts">
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import { hasNextPage } from "@/utils/paginationUtils/paginationUtils";
    import { lastValueFrom, map } from "rxjs";
    import { createEventDispatcher } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";
    import InfoIcon from "@components/icons/InfoIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import Slider from "@components/Slider/Slider.svelte";
    import SiemaSliderController from "@components/Slider/SliderController";
    import T from "@components/T.svelte";

    import ReviewsImagesSliderQuery from "../../queries/ReviewsImagesSliderQuery";
    import ReviewsImagesSliderService from "../../services/ReviewsImagesSliderService";
    import ReviewsImagesSliderArrows from "./ReviewsImagesSliderArrows.svelte";

    export let reviewsImagesSliderService: ReviewsImagesSliderService;
    export let reviewsImagesSliderQuery: ReviewsImagesSliderQuery;
    export let minImagesForSliderVisibility: number;
    export let productId: number | null;
    export let isIncentivizedEnabled: boolean;

    const sliderController = new SiemaSliderController();
    const hasNext$ = sliderController.selectHasNext();
    const hasPrev$ = sliderController.selectHasPrev();
    const dispatch = createEventDispatcher();

    const reviewsImagesList$ = reviewsImagesSliderQuery.all$;
    const reviewsImagesPaginationMeta$ =
        reviewsImagesSliderQuery.paginationMeta$;
    const reviewsImagesStatus$ = reviewsImagesSliderQuery.status$;

    const loadMoreReviewsImagesSlider = () => {
        if (productId === null) {
            throw new Error("Product ID is not defined");
        }

        lastValueFrom(
            reviewsImagesSliderService.loadMoreReviewsImagesSlider({
                productId,
                sortingOptions: ["mostRecent"],
                onlyWithPhotos: true,
                offset:
                    $reviewsImagesPaginationMeta$.perPage +
                    $reviewsImagesPaginationMeta$.currentOffset,
                perPage: 10,
            })
        );
    };

    const reviewsImages$ = reviewsImagesList$.pipe(
        map((images) => {
            return images.flatMap((review) => {
                return review.images.map((image, i) => {
                    return {
                        ...image,
                        index: i,
                        reviewUid: review.uid,
                        isReviewIncentivized: review.isIncentivized,
                        reviewTitle: review.title,
                    };
                });
            });
        })
    );

    const handleNext = () => {
        if (!$hasNext$ && hasNextPage($reviewsImagesPaginationMeta$)) {
            loadMoreReviewsImagesSlider();
        }
        sliderController.next();
    };

    const handlePrev = () => {
        sliderController.prev();
    };

    const handleClick = (reviewUid: string, activeImageIndex: number) => {
        dispatch("openImageReview", {
            reviewUid,
            activeImageIndex,
        });
    };
</script>

{#if $reviewsImages$.length >= minImagesForSliderVisibility}
    <div class="gw-rv-reviews-images-slider">
        {#key $reviewsImages$.length}
            <Slider
                {sliderController}
                perPage={{
                    320: 2,
                    576: 3,
                    768: 4,
                    992: 6,
                    1200: 7,
                }}
                duration={450}
                loop={false}
            >
                <svelte:fragment slot="slides">
                    {#each $reviewsImages$ as image, imageIdx (`${image.reviewUid}-${image.id}`)}
                        <div class="gw-rv-reviews-images-slider__item">
                            <BaseButton
                                class="gw-rv-reviews-images-slider__item-image-wrapper"
                                on:click={() =>
                                    handleClick(image.reviewUid, image.index)}
                                ariaLabel={"Open review " + image.reviewTitle}
                            >
                                <Image
                                    src={getImageSizeUrl(image.sizes, "md") ||
                                        ""}
                                    alt={`product review image ${imageIdx + 1}`}
                                    objectFit="cover"
                                />
                                {#if isIncentivizedEnabled && image.isReviewIncentivized}
                                    <div
                                        class="gw-rv-reviews-images-slider__item-incentivized-wrapper"
                                    >
                                        <div
                                            class="gw-rv-reviews-images-slider__item-incentivized"
                                        >
                                            <T
                                                namespace="reviews.ReviewsProductPageWidget"
                                                translationKey="incentivized"
                                            />
                                            <Icon
                                                width="14"
                                                height="14"
                                                color="#ffffff"
                                                stroke="#ffffff"
                                            >
                                                <InfoIcon />
                                            </Icon>
                                        </div>
                                    </div>
                                {/if}
                            </BaseButton>
                        </div>
                    {/each}
                </svelte:fragment>
                <ReviewsImagesSliderArrows
                    slot="arrows"
                    hasNext={$hasNext$ ||
                        hasNextPage($reviewsImagesPaginationMeta$)}
                    hasPrev={$hasPrev$}
                    status={$reviewsImagesStatus$}
                    on:nextSlide={handleNext}
                    on:prevSlide={handlePrev}
                />
            </Slider>
        {/key}
    </div>
{/if}

<style lang="postcss">
    .gw-rv-reviews-images-slider {
        margin-bottom: 2.5em;
        max-width: 100%;

        &__item {
            position: relative;
            width: 100%;
            display: flex;
            justify-content: center;
            overflow: hidden;

            &:hover :global(img) {
                transform: scale(1.1);
            }

            &-incentivized-wrapper {
                position: absolute;
                bottom: 0.25em;
                right: 0.75em;
            }

            &-incentivized {
                display: flex;
                align-items: center;
                gap: 0.25rem;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
                letter-spacing: 0.36px;
                padding: 2px 8px;
                border-radius: 4px;
                opacity: 0.6;
                background: rgba(18, 18, 18, 0.9);
                color: #ffffff;
            }

            :global(&-image-wrapper) {
                width: 134px;
                height: 134px;
                transition: transform 0.2s;
                border-radius: 4px;
                overflow: hidden;

                @media screen and (min-width: 320px) {
                    width: 150px;
                }

                @media screen and (min-width: 576px) {
                    width: 136px;
                }
            }
        }
    }
</style>
